<script context="module">
  export const prerender = true;
</script>

<script lang="ts">
  import { onMount } from "svelte";
  import {
    userSignedIn,
    userData,
    apiData,
  } from "../store";
  import { fetchData, getAPISubscription } from "../firebaseOperations";
  import Footer from "../components/Footer.svelte";
  let apikey = 'YOUR_API_KEY';

  const apiInfo = {
    endpoint: "https://api.text-cloaker.com/cloakText",
    methods: ["POST"],
    auth: "Bearer YOUR_API_KEY",
    input: {
      text: "The text you want to cloak. For best results, keep the text above 2000 characters."
    },
    output: {
      date: "The date and time of the request.",
      cloakedText: "The cloaked version of the input text.",
      usedQuota: "The total cloaked characters used by this API key after this request.",
      remainingQuota: "The remaining quota of cloaked characters for this API key after this request."
    },
    rateLimits: {
      starter: "10 cloak requests / minute",
      growth: "25 cloak requests / minute",
      scale: "50 cloak requests / minute",
      ultimate: "100 cloak requests / minute"
    },
    errors: {
    401: "No API key provided",
    404: "API key not found",
    429: "Too Many Requests (Rate limit exceeded)",
  }
  };

  let sessionId: any;
  let urlPromoCode: any;

  const urlParams = new URLSearchParams(window.location.search);
  sessionId = urlParams.get('session_id');
  urlPromoCode = urlParams.get('code');

  onMount(() => {
    const googleToken = localStorage.getItem("googleToken");
    if (googleToken) {
      fetchData(googleToken).then(async (data) => {
        $userData = data;
        if($userData.id){
          $userSignedIn = true;
        }
        let api_data = await getAPISubscription($userData.id);
        api_data = api_data.apiData;
        if (api_data.quota > 0) {
            $apiData = api_data;
            apikey = api_data.apiKey;
            document.getElementById("signinforsnippets").style.display = "none";
        }
      });
    }
  });

  window.history.replaceState({}, document.title, "/");
</script>

<div class="container">
  <div class="content">
    <div class="header">
      <h1>TextCloaker API Documentation</h1>
      <p>Implement the TextCloaker API in various programming languages.</p>
      <p id="signinforsnippets">Sign in using the profile icon in the top right to access personalized code snippets!</p>
    </div>

    <div class="main-content">
      <div class="left-column">
        <div class="section">
          <h2>API Overview</h2>
          <table class="info-table">
            <tr>
              <td><strong>Endpoint</strong></td>
              <td>{apiInfo.endpoint}</td>
            </tr>
            <tr>
              <td><strong>Methods</strong></td>
              <td>{apiInfo.methods.join(", ")}</td>
            </tr>
            <tr>
              <td><strong>Authentication</strong></td>
              <td>{apiInfo.auth}</td>
            </tr>
          </table>
        </div>
        
        <div class="section">
          <h2>Input</h2>
          <table class="info-table">
            <tr>
              <td><strong>text</strong> (String)</td>
              <td>{apiInfo.input.text}</td>
            </tr>
          </table>
        </div>
        
        <div class="section">
          <h2>Output</h2>
          <table class="info-table">
            <tr>
              <td><strong>date</strong> (Date)</td>
              <td>{apiInfo.output.date}</td>
            </tr>
            <tr>
              <td><strong>cloakedText</strong> (String)</td>
              <td>{apiInfo.output.cloakedText}</td>
            </tr>
            <tr>
              <td><strong>usedQuota</strong> (Integer)</td>
              <td>{apiInfo.output.usedQuota}</td>
            </tr>
            <tr>
              <td><strong>remainingQuota</strong> (Integer)</td>
              <td>{apiInfo.output.remainingQuota}</td>
            </tr>
          </table>
        </div>

        <div class="section">
          <h2>Errors</h2>
          <table class="info-table">
            {#each Object.entries(apiInfo.errors) as [code, message]}
              <tr>
                <td>Code <strong>{code}</strong></td>
                <td>{message}</td>
              </tr>
            {/each}
          </table>
        </div>

        <div class="section">
          <h2>Quotas and Rate Limits</h2>
          <table class="info-table">
            <tr>
              <td><strong>Starter</strong></td>
              <td>{apiInfo.rateLimits.starter}</td>
            </tr>
            <tr>
              <td><strong>Growth</strong></td>
              <td>{apiInfo.rateLimits.growth}</td>
            </tr>
            <tr>
              <td><strong>Scale</strong></td>
              <td>{apiInfo.rateLimits.scale}</td>
            </tr>
            <tr>
              <td><strong>Ultimate</strong></td>
              <td>{apiInfo.rateLimits.ultimate}</td>
            </tr>
          </table>
        </div>
      </div>

      <div class="right-column">
        <div class="section">
          <h2>Code Examples</h2>
          
          <div class="code-example">
            <div class="language">Node.js</div>
            <div class="code-block">
              <pre>
                <code>{@html `
const inputText = "Your text here";

import('node-fetch').then(({ default: fetch }) => {
    fetch('https://api.text-cloaker.com/cloakText', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ${apikey === 'YOUR_API_KEY' ? apikey : `<span class="highlight">${apikey}</span>`}'
        },
        body: JSON.stringify({ text: inputText })
    })
    .then(response => response.json())
    .then(data => console.log(data))
    .catch(error => console.error('Error:', error));
});
                `}</code>
              </pre>
            </div>
          </div>
        
          <div class="code-example">
            <div class="language">Python</div>
            <div class="code-block">
              <pre>
                <code>{@html `
import requests

input_text = "Your text here"

response = requests.post(
  'https://api.text-cloaker.com/cloakText',
  headers={
    'Content-Type': 'application/json',
    'Authorization': 'Bearer ${apikey === 'YOUR_API_KEY' ? apikey : `<span class="highlight">${apikey}</span>`}'
  },
  json={'text': input_text}
)

print(response.json())
                `}</code>
              </pre>
            </div>
          </div>
        
          <div class="code-example">
            <div class="language">Curl</div>
            <div class="code-block">
              <pre>
                <code>{@html `
curl -X POST https://api.text-cloaker.com/cloakText
-H "Content-Type: application/json"
-H "Authorization: Bearer ${apikey === 'YOUR_API_KEY' ? apikey : `<span class="highlight">${apikey}</span>`}"
-d '{"text":"Your text here"}'
                `}</code>
              </pre>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <p class="center-text">If you're having trouble using the API or have any questions, please contact us at <a href="https://aiobfuscation.com">aiobfuscation.com</a>.</p>
    <Footer />
  </div>
</div>

<style>
  .container {
    position: relative;
    z-index: 1;
  }
  .content {
    font-family: Arial, sans-serif;
    max-width: 1200px;
    margin: 0 auto;
    padding: 20px;
  }
  .header {
    text-align: center;
    margin-bottom: 40px;
  }
  .main-content {
    display: flex;
    gap: 40px;
  }
  .left-column, .right-column {
    flex: 1;
  }
  .section {
    margin-bottom: 30px;
    background-color: #333;
    padding: 20px;
    border-radius: 5px;
  }
  h2 {
    margin-top: 0;
    margin-bottom: 15px;
    color: #fff;
  }
  .info-table {
    width: 100%;
    border-collapse: collapse;
  }
  .info-table td {
    padding: 8px;
    border-bottom: 1px solid #444;
  }
  .info-table td:first-child {
    font-weight: bold;
    width: 40%;
  }
  .code-example {
    margin-bottom: 20px;
  }
  .language {
    font-weight: bold;
    margin-bottom: 5px;
  }
  .code-block {
    background-color: #686868;
    padding: 10px;
    border-radius: 5px;
    text-align: left;
  }
  pre {
    margin: 0;
    white-space: pre-wrap;
    word-wrap: break-word;
  }
  .center-text {
    text-align: center;
  }
  .highlight {
    background-color: yellow;
    color: black;
  }
</style>